import { useEffect, useState, useCallback } from "react";
import { MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/Common";
import { ChatThread } from "@/components/ChatThread";
import { api, formatErr, fmtDate, useFetch } from "@/lib/api";

export default function AdminMessages() {
  const { data: threads, reload } = useFetch("/admin/threads");
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);

  const loadThread = useCallback(async (bid) => {
    const { data } = await api.get(`/admin/threads/${bid}`);
    setMsgs(data);
  }, []);

  useEffect(() => {
    if (!active) return;
    loadThread(active.business_id);
    const t = setInterval(() => { loadThread(active.business_id); reload(); }, 5000);
    return () => clearInterval(t);
  }, [active, loadThread, reload]);

  const send = async (body) => {
    try {
      const { data } = await api.post(`/admin/threads/${active.business_id}`, { body });
      setMsgs((p) => [...p, data]);
      reload();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Inbox" title="Client messages" desc="Reply to business owners from one place." />
      {threads?.length === 0 ? <EmptyState testId="threads-empty" icon={MessageSquare} title="No conversations yet" /> : (
        <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
          <div className="h-fit overflow-hidden rounded-md border border-stone-200 bg-white divide-y divide-stone-100">
            {(threads || []).map((t) => (
              <button key={t.business_id} data-testid={`thread-${t.business_id}`} onClick={() => setActive(t)}
                className={`block w-full px-5 py-4 text-left transition-colors ${active?.business_id === t.business_id ? "bg-stone-100" : "hover:bg-stone-50"}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate font-medium text-stone-900">{t.company || t.name}</p>
                  {t.unread > 0 && <span className="rounded-full bg-amber-500 px-2 text-[11px] font-bold text-slate-900">{t.unread}</span>}
                </div>
                <p className="truncate text-xs text-stone-500">{t.last}</p>
                <p className="mt-1 text-[10px] font-mono text-stone-400">{fmtDate(t.last_at)}</p>
              </button>
            ))}
          </div>
          {active ? (
            <div>
              <p className="mb-3 text-sm text-stone-600"><span className="font-medium text-stone-900">{active.name}</span> · {active.email}</p>
              <ChatThread messages={msgs} onSend={send} meRole="admin" emptyText="No messages" />
            </div>
          ) : <EmptyState testId="thread-placeholder" icon={MessageSquare} title="Select a conversation" />}
        </div>
      )}
    </div>
  );
}
